import {
  IconCalendar,
  IconClipboardCheck,
  IconMail,
  IconSchool,
  IconSettings,
  IconShield,
  IconTestPipe,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";

import { DevelopmentFeatureFlags, PracticeFeatureFlags } from "@/feature-flags";

import type { DestinationAccess, NavigationDestination } from "../types/destinations";

import { MODULE_DESTINATION_IDS, type ModuleDestinationId } from "./module-destination-ids";

/**
 * Every top-level module in the side nav, in nav order. The order puts each gated module where it
 * sits for a user who passes every gate.
 */
export const MODULE_DESTINATIONS: ModuleDestination[] = [
  {
    id: MODULE_DESTINATION_IDS.profile,
    label: "Profile",
    Icon: IconUser,
    isSearchable: false,
    gates: { audiences: ["patient"] },
    buildPath: ({ profileId }) => `/clients/${profileId}/profile`,
  },
  {
    id: MODULE_DESTINATION_IDS.clients,
    label: "Clients",
    keywords: ["patients", "charts", "records", "people"],
    Icon: IconUsers,
    buildPath: () => "/clients",
  },
  {
    id: MODULE_DESTINATION_IDS.schedule,
    label: "Schedule",
    keywords: ["calendar", "appointments", "bookings"],
    Icon: IconCalendar,
    buildPath: (access) => buildPracticePath(access, "scheduling"),
  },
  {
    id: MODULE_DESTINATION_IDS.admin,
    label: "Admin",
    Icon: IconShield,
    isSearchable: false,
    gates: { requiredRole: "project-admin" },
    buildPath: () => "/admin/project",
  },
  {
    id: MODULE_DESTINATION_IDS.practiceBilling,
    label: "Practice Billing",
    keywords: ["claims", "statements", "payments", "invoices"],
    Icon: IconClipboardCheck,
    gates: {
      practiceFeatures: [PracticeFeatureFlags.Billing],
      developmentFeatures: [DevelopmentFeatureFlags.PracticeBilling],
      requiredRole: "any-permission",
    },
    buildPath: (access) => buildPracticePath(access, "billing"),
  },
  {
    id: MODULE_DESTINATION_IDS.messages,
    label: "Messages",
    keywords: ["inbox", "chat", "conversations"],
    Icon: IconMail,
    gates: { audiences: ["practitioner", "patient"] },
    buildPath: (access) => {
      if (access.audience === "patient") return "/messages";
      return buildPracticePath(access, "messages");
    },
  },
  {
    id: MODULE_DESTINATION_IDS.resources,
    label: "Resources",
    keywords: ["education", "handouts", "content"],
    Icon: IconSchool,
    gates: {
      audiences: ["practitioner", "patient"],
      developmentFeatures: [DevelopmentFeatureFlags.EduResources],
    },
    buildPath: (access) => {
      if (access.audience === "patient") return `/clients/${access.profileId}/edu-resources`;
      return buildPracticePath(access, "edu-resources");
    },
  },
  {
    id: MODULE_DESTINATION_IDS.tests,
    label: "Tests",
    keywords: ["labs", "results", "lab orders"],
    Icon: IconTestPipe,
    gates: { requiredRole: "provider-or-scribe" },
    buildPath: (access) => buildPracticePath(access, "tests"),
  },
  {
    id: MODULE_DESTINATION_IDS.settings,
    label: "Settings",
    keywords: ["preferences", "configuration", "setup"],
    Icon: IconSettings,
    buildPath: (access) => buildPracticePath(access, "settings"),
  },
];

/**
 * Builds a path under the active practice.
 *
 * @param access - The user's access facts; its `practiceId` names the practice.
 * @param section - The practice section, such as `billing`.
 * @returns The path, such as `/practices/abc/billing`.
 */
function buildPracticePath({ practiceId }: DestinationAccess, section: string): string {
  return `/practices/${practiceId}/${section}`;
}

export type ModuleDestination = NavigationDestination & { id: ModuleDestinationId };
